// ============================================================================
// TOKEN MANAGER — Centralized Token Lifecycle Management
// ============================================================================
// Manages access tokens (in-memory + localStorage fallback for page reload),
// refresh tokens (localStorage), and tenant context.

import { STORAGE_KEYS } from './storageKeys';

interface JWTPayload {
  exp?: number;
  iat?: number;
  sub?: string;
  [key: string]: any;
}

// In-memory access token — avoids XSS-accessible localStorage reads on every request
let inMemoryAccessToken: string | null = null;

export const tokenManager = {
  // ============================================================================
  // ACCESS TOKEN
  // ============================================================================

  getAccessToken(): string | null {
    if (inMemoryAccessToken) {
      return inMemoryAccessToken;
    }
    // Fallback to localStorage for page reload recovery
    const stored = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (stored) {
      inMemoryAccessToken = stored;
    }
    return stored;
  },

  setAccessToken(token: string): void {
    inMemoryAccessToken = token;
    localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, token);
  },

  clearAccessToken(): void {
    inMemoryAccessToken = null;
    localStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN);
  },

  // ============================================================================
  // REFRESH TOKEN
  // ============================================================================

  getRefreshToken(): string | null {
    return localStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  setRefreshToken(token: string): void {
    localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, token);
  },

  clearRefreshToken(): void {
    localStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
  },

  // ============================================================================
  // TENANT CONTEXT
  // ============================================================================

  getTenantId(): string | null {
    return localStorage.getItem(STORAGE_KEYS.TENANT_ID);
  },

  setTenantId(id: string): void {
    localStorage.setItem(STORAGE_KEYS.TENANT_ID, id);
  },

  getAppId(): string | null {
    return localStorage.getItem(STORAGE_KEYS.APP_ID);
  },

  setAppId(id: string): void {
    localStorage.setItem(STORAGE_KEYS.APP_ID, id);
  },

  getUserId(): string | null {
    return localStorage.getItem(STORAGE_KEYS.USER_ID);
  },

  setUserId(id: string): void {
    localStorage.setItem(STORAGE_KEYS.USER_ID, id);
  },

  // ============================================================================
  // JWT UTILITIES (no external library — uses atob for decode)
  // ============================================================================

  decodeToken(token: string): JWTPayload | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const payload = parts[1];
      // Handle base64url encoding
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const jsonStr = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join('')
      );
      return JSON.parse(jsonStr);
    } catch {
      return null;
    }
  },

  isTokenExpired(token: string): boolean {
    const payload = this.decodeToken(token);
    if (!payload?.exp) return true;
    // Token is expired if current time is past expiration
    return Date.now() >= payload.exp * 1000;
  },

  getTimeUntilExpiry(token: string): number | null {
    const payload = this.decodeToken(token);
    if (!payload?.exp) return null;
    return payload.exp * 1000 - Date.now();
  },

  // ============================================================================
  // SESSION
  // ============================================================================

  isAuthenticated(): boolean {
    const token = this.getAccessToken();
    if (!token) return false;
    return !this.isTokenExpired(token);
  },

  clearAll(): void {
    inMemoryAccessToken = null;
    // Remove all known storage keys
    Object.values(STORAGE_KEYS).forEach((key) => {
      localStorage.removeItem(key);
    });
  },
};

export default tokenManager;
